import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { resetPassword, sendPasswordResetEmail } from 'base/actions/asynchronous'
import { setSessionMessage, clearSessionMessage } from 'base/actions/synchronous'
import SiteLocation from 'base/constants/site_location'
import PasswordResetEmailForm from 'base/components/forms/password_reset_email_form'
import PasswordResetPasswordForm from 'base/components/forms/password_reset_password_form'
import FlatButton from 'base/react/components/flat_button'
import Validate from 'base/lib/validate'
import routeTo from 'base/lib/route_to'

class PasswordReset extends Component {
  static propTypes = {
    sendPasswordResetEmail: PropTypes.func,
    resetPassword: PropTypes.func,
    setBannerMessage: PropTypes.func,
    clearBannerMessage: PropTypes.func,
    signIn: PropTypes.func,
    email: PropTypes.string,
    token: PropTypes.string,
    exitReset: PropTypes.func,
    isPasswordlessUser: PropTypes.bool,
  }

  static defaultProps = {
    token: null,
    exitReset: () => routeTo('/'),
  }

  state = {
    email: this.props.email,
    emailEntered: false,
    password: '',
    error: false,
    errorMessage: '',
    passwordRevealed: false,
  }

  getValidationErrors = () => {
    const validator = new Validate({ verboseErrors: false })
    validator.validate({
      email: {
        value: this.state.email,
        label: 'Email Address',
        validations: {
          presence: true,
          pattern: 'email',
        },
      },
    })

    return validator.getSentenceErrors()
  }

  handleEmailSubmit = async () => {
    this.setState({ error: false, errorMessage: '' })

    const emailError = Object.values(this.getValidationErrors())[0]

    if (emailError) {
      return this.setState({ error: true, errorMessage: emailError })
    }

    await this.props.sendPasswordResetEmail(this.state.email)
    this.setState({
      emailEntered: true,
    })
  }

  handlePasswordSubmit = async () => {
    const reset = await this.props.resetPassword(this.state.password)

    if (reset.user) {
      this.handleLoginUser(reset)
    } else {
      this.handleError(reset.message)
    }
  }

  handleLoginUser = () => {
    this.props.setBannerMessage({
      message: 'Your password has been reset and you are logged in.',
      error: false,
    })

    routeTo('/')

    window.setTimeout(() => {
      window.setTimeout(this.props.clearBannerMessage, 5000)
    }, 500)
  }

  handleError = error => {
    this.props.setBannerMessage({
      message: error,
      error: true,
    })

    window.setTimeout(this.props.clearBannerMessage, 5000)
  }

  handleEmailChange = event => {
    this.setState({ email: event.target.value })
  }

  handlePasswordChange = event => {
    this.setState({ password: event.target.value })
  }

  handleTogglePassword = () => {
    this.setState({ passwordRevealed: !this.state.passwordRevealed })
  }

  renderText() {
    if (this.props.token) {
      return {
        headline: 'Set a new password',
        body: 'Enter your new password below.',
      }
    } else if (this.state.emailEntered) {
      return {
        headline: 'Check your email',
        body: `If an account with ${this.state.email} exists, a password reset link has been sent. If you don't see this email, please check your spam folder.`,
      }
    } else if (this.props.isPasswordlessUser) {
      return {
        headline: 'Set a password',
        body: 'You need to set up a password to complete account setup and access past orders. We will send you an email with a link to set your password.',
      }
    } else {
      return {
        headline: 'Reset password',
        body: 'Enter the email address associated with your account to receive a link to reset your password.',
      }
    }
  }

  renderEmailOrPasswordForm() {
    if (this.props.token) {
      return this.renderPasswordForm()
    } else if (this.state.emailEntered) {
      return this.renderLoginButton()
    } else {
      return this.renderEmailForm()
    }
  }

  renderEmailForm() {
    return (
      <PasswordResetEmailForm
        email={this.state.email}
        onSubmit={this.handleEmailSubmit}
        onChange={this.handleEmailChange}
        error={this.state.errorMessage}
      />
    )
  }

  renderPasswordForm() {
    return (
      <PasswordResetPasswordForm
        password={this.state.password}
        onSubmit={this.handlePasswordSubmit}
        onChange={this.handlePasswordChange}
        error={this.state.errorMessage}
        passwordRevealed={this.state.passwordRevealed}
        onTogglePassword={this.handleTogglePassword}
      />
    )
  }

  renderLoginButton() {
    return (
      <FlatButton
        color="dark-grey"
        onClick={this.props.exitReset}
        className="password-reset__button"
      >
        Go Back To Login
      </FlatButton>
    )
  }

  render() {
    return (
      <div className="password-reset">
        <div className="password-reset__container">
          <h2 className="password-reset__title">{this.renderText().headline}.</h2>
          <p className="password-reset__body">{this.renderText().body}</p>

          {this.renderEmailOrPasswordForm()}
        </div>
      </div>
    )
  }
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    sendPasswordResetEmail: email => dispatch(sendPasswordResetEmail(email)),
    resetPassword: password =>
      dispatch(resetPassword(ownProps.token, password, SiteLocation.PASSWORD_RESET)),
    setBannerMessage: ({ message, error }) => dispatch(setSessionMessage(message, error)),
    clearBannerMessage: () => dispatch(clearSessionMessage()),
  }
}

export default connect(null, mapDispatchToProps)(PasswordReset)
